import { useRef, useState } from 'react';
import { Check, Copy, Download, FolderInput, ListChecks, Tag, Trash2, X, ChevronDown, Archive } from 'lucide-react';
import type { ApprovalStatus } from '@/lib/api/types';
import { useSelection } from '@/stores/selection';
import { Button, IconButton } from '@/components/Button';
import { Menu } from '@/components/Menu';
import { APPROVAL_TONE } from '@/components/Misc';
import { usePermission } from '@/features/workspace/usePermission';
import { DockSlot } from '@/features/workspace/BottomDock';
import type { AssetActionKind, AssetActionOpts } from './useAssetActions';

const STATUSES: ApprovalStatus[] = ['none', 'in_progress', 'needs_review', 'approved'];

/** §2.10 bulk toolbar; appears when `ids.size > 0`. */
export function BulkToolbar({ boardId, run }: { boardId: string | null; run: (kind: AssetActionKind, ids: string[], opts?: AssetActionOpts) => void }) {
  const ids = useSelection((s) => s.ids);
  const clear = useSelection((s) => s.clear);
  const [approvalMenu, setApprovalMenu] = useState(false);
  const approvalBtn = useRef<HTMLButtonElement>(null);
  const canEdit = usePermission('EDIT_METADATA', { boardId });
  const canApprove = usePermission('APPROVE', { boardId });
  const canDownload = usePermission('DOWNLOAD', { boardId });
  const canManage = usePermission('MANAGE_BOARD', { boardId });
  if (ids.size === 0) return null;
  const list = [...ids].filter((k) => !k.startsWith('cu_'));
  return (
    <DockSlot>
    <div role="toolbar" aria-label="Selection actions" className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-1 rounded-xl border border-line bg-bg-elev px-2 py-1.5 shadow-lg">
      <span className="px-2 text-sm font-medium tabular-nums" aria-live="polite">
        {ids.size} selected
      </span>
      {canManage && (
        <Button size="sm" onClick={() => run('move', list)} title="Move to board">
          <FolderInput size={14} /> Move
        </Button>
      )}
      {canManage && (
        <Button size="sm" onClick={() => run('copy', list)} title="Copy to board">
          <Copy size={14} /> Copy
        </Button>
      )}
      {canEdit && (
        <Button size="sm" onClick={() => run('tag', list)}>
          <Tag size={14} /> Tag
        </Button>
      )}
      {canEdit && (
        <Button size="sm" onClick={() => run('field', list)} title="Set a custom field on the selection">
          <ListChecks size={14} /> Set field…
        </Button>
      )}
      {canEdit && <SetRightsButton list={list} /> /* release2/C */}
      {canEdit && <InternalOnlyButton list={list} /> /* release3/B */}
      {(canApprove || canEdit) && (
        <>
          <Button ref={approvalBtn} size="sm" onClick={() => setApprovalMenu(true)} aria-haspopup="menu" aria-expanded={approvalMenu}>
            <Check size={14} /> Approval <ChevronDown size={12} />
          </Button>
          {approvalMenu && (
            <Menu
              anchor={approvalBtn.current}
              onClose={() => setApprovalMenu(false)}
              items={STATUSES.filter((s) => s !== 'approved' || canApprove).map((s) => ({ label: APPROVAL_TONE[s].label, icon: <span className={`inline-block h-2.5 w-2.5 rounded-full ${APPROVAL_TONE[s].dot}`} />, onSelect: () => run('approval', list, { status: s }) }))}
            />
          )}
        </>
      )}
      {canDownload && <ZipButton list={list} run={run} /> /* release3/C: zip dialog with the preset picker */}
      {/* release4/X */}
      <ExportCsvButton list={list} />
      {canManage && <ArchiveButton list={list} run={run} /> /* release1/B */}
      {canManage && (
        <Button size="sm" variant="ghost" className="text-danger" onClick={() => run('delete', list)}>
          <Trash2 size={14} /> Delete
        </Button>
      )}
      <IconButton label="Clear selection" size="sm" onClick={clear}>
        <X size={14} />
      </IconButton>
    </div>
    </DockSlot>
  );
}

// --- release1/B ---
/** S7a: archive the selection (undo via toast in useAssetActions). */
function ArchiveButton({ list, run }: { list: string[]; run: (kind: AssetActionKind, ids: string[]) => void }) {
  return (
    <Button size="sm" onClick={() => run('archive', list)} title="Archive: hide from boards and search without deleting">
      <Archive size={14} /> Archive
    </Button>
  );
}

// --- release2/C ---
import { ShieldCheck } from 'lucide-react';
import { BulkRightsDialog } from '@/features/rights/BulkRightsDialog';
/** C4: set usage rights on the selection (merge by default; per-asset EDIT_METADATA failures are reported). */
function SetRightsButton({ list }: { list: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} title="Set usage rights on the selection" data-bulk-set-rights>
        <ShieldCheck size={14} /> Rights…
      </Button>
      {open && <BulkRightsDialog ids={list} onClose={() => setOpen(false)} />}
    </>
  );
}

// --- release3/B --- DEL-1 "Internal only" on the selection (bulk `set_share_excluded`; per-asset EDIT_METADATA failures are reported)
import { EyeOff } from 'lucide-react';
import { useBulkSetShareExcluded } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
function InternalOnlyButton({ list }: { list: string[] }) {
  const [menu, setMenu] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  return (
    <>
      <Button ref={btn} size="sm" onClick={() => setMenu(true)} aria-haspopup="menu" aria-expanded={menu} title="Hide from or show through share links" data-bulk-share-excluded>
        <EyeOff size={14} /> Sharing <ChevronDown size={12} />
      </Button>
      {menu && <InternalOnlyMenu list={list} anchor={btn.current} onClose={() => setMenu(false)} />}
    </>
  );
}
/** Mounted only while the menu is open, so the toolbar itself needs no QueryClient (BottomDock renders it bare). */
function InternalOnlyMenu({ list, anchor, onClose }: { list: string[]; anchor: HTMLElement | null; onClose: () => void }) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const bulk = useBulkSetShareExcluded(wsId);
  const apply = (value: boolean) =>
    bulk.mutate(
      { ids: list, value },
      {
        onSuccess: (r) => {
          const n = r.succeeded.length;
          if (r.failed.length) toast({ kind: 'error', title: `${r.failed.length} of ${list.length} could not be changed`, body: r.failed[0]?.message });
          else toast({ kind: 'success', title: value ? `${n} marked internal only` : `${n} shareable again` });
        },
        onError: () => toast({ kind: 'error', title: 'Could not update the selection' }),
      },
    );
  return (
    <Menu
      anchor={anchor}
      onClose={onClose}
      items={[
        { label: 'Internal only (hide from share links)', onSelect: () => apply(true) },
        { label: 'Shareable (show through share links)', onSelect: () => apply(false) },
      ]}
    />
  );
}

// --- release3/C ---
import { ZipPresetDialog } from '@/features/export/ZipPresetDialog';
/** EXP-1: "Zip" opens the preset picker (originals, or images through a preset) before starting the bulk zip job. */
function ZipButton({ list, run }: { list: string[]; run: (kind: AssetActionKind, ids: string[], opts?: AssetActionOpts) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} title="Download the selection as a zip" data-bulk-zip>
        <Download size={14} /> Zip
      </Button>
      {open && (
        <ZipPresetDialog
          count={list.length}
          onClose={() => setOpen(false)}
          onConfirm={(presetId) => {
            setOpen(false);
            run('download', list, { presetId });
          }}
        />
      )}
    </>
  );
}

// --- release4/X --- EXP-2 "Export CSV": metadata CSV of the selection as an `export_csv` job (VIEW per asset, so no gate here);
// plain fetch + the job toast, so the bare toolbar needs no QueryClient
import { FileSpreadsheet } from 'lucide-react';
import { exportSelectionCsv } from './csvExportJob';
function ExportCsvButton({ list }: { list: string[] }) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      disabled={busy || !wsId}
      title="Export the selection's metadata as a CSV file"
      data-bulk-export-csv
      onClick={() => {
        setBusy(true);
        void exportSelectionCsv(wsId, list).finally(() => setBusy(false));
      }}
    >
      <FileSpreadsheet size={14} /> Export CSV
    </Button>
  );
}
